<template>
  <div>
    <v-row>
      <v-col>
        <h2 class="mb-3 mt-3 ml-3">{{ $t('account.userAccount')}}</h2>

        <v-card elevation=0 class="pa-4">
          <v-row>
            <v-col cols="12" md="4"  class="d-flex align-center">
              {{ $t('account.firstName') }}
            </v-col>
            <v-col cols="12" md="8" lg="6" xl=4 class="d-flex align-center">
              <v-text-field
                  v-model="firstName"
                  :readonly="isFirstNameDisabled"
                  :rules="firstNameRules"
                  :error-messages="firstNameError"
                  hide-details="auto"
                  variant="solo-filled"
                  flat
              >
                <template v-slot:append-inner>
                  <v-btn variant="text" density="compact" @click="toggleFirstNameField">
                    {{ isFirstNameDisabled ? $t('account.edit') : $t('account.save') }}
                  </v-btn>

                  <v-btn
                    v-if="!isFirstNameDisabled"
                    density="compact"
                    width="36"
                    height="36"
                    variant="text"
                    rounded="circle"
                    icon
                    @click="cancelFirstNameEdit"
                  >
                    <v-icon>mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4"  class="d-flex align-center">
              {{ $t('account.lastName') }}
            </v-col>
            <v-col cols="12" md="8" lg="6" xl=4 class="d-flex align-center">
              <v-text-field
                  v-model="lastName"
                  :readonly="isLastNameDisabled"
                  :rules="lastNameRules"
                  :error-messages="lastNameError"
                  hide-details="auto"
                  variant="solo-filled"
                  flat
              >

                <template v-slot:append-inner>
                  <v-btn variant="text" density="compact" @click="toggleLastNameField">
                    {{ isLastNameDisabled ? $t('account.edit') : $t('account.save') }}
                  </v-btn>

                  <v-btn
                    v-if="!isLastNameDisabled"
                    width="36"
                    height="36"
                    variant="text"
                    rounded="circle"
                    icon
                    @click="cancelLastNameEdit"
                  >
                    <v-icon>mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4"  class="d-flex align-center" >
              {{ $t('account.emailAddress') }}
            </v-col>
            <v-col cols="12" md="8" lg="6" xl=4 class="d-flex align-center">
              <v-text-field
                  :readonly="isEmailAddressDisabled"
                  v-model="emailAddress"
                  :rules="emailAddressRules"
                  :error-messages="emailAddressError"
                  hide-details="auto"
                  variant="solo-filled"
                  flat
              >
              </v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="auto">
              <ChangePasswordDialog>
                <v-btn color="primary" elevation=0>
                  {{$t('account.changePassword')}}
                </v-btn>
              </ChangePasswordDialog>
            </v-col>

            <v-col cols="auto">
              <EnableMFADialog v-if="!isMFAEnabled" v-model:is-processing="isProcessing">
                <v-btn color="primary" variant="outlined" v-bind:disabled="isProcessing">
                  {{$t('menus.authentication.enableMFA')}}
                </v-btn>
              </EnableMFADialog>

              <DisableMFADialog v-if="isMFAEnabled" v-model:is-processing="isProcessing" >
                <v-btn color="primary" variant="outlined" v-bind:disabled="isProcessing">
                  {{$t('menus.authentication.disableMFA')}}
                </v-btn>
              </DisableMFADialog>
            </v-col>

            <v-col v-if="canShowSubscriptionType" cols="auto">
              <v-btn color="primary" elevation=0 @click="openSubscriptionContactDialog">
                {{getSubscriptionName}} user
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <h2 class="mb-3 mt-3 ml-3">{{ $t('account.notificationSettings')}}</h2>

        <v-card elevation=0 class="pa-4" style="">
          <v-row>
            <v-col cols="8" md="6" class="d-flex align-center">
              {{ $t('account.newCommentNotification')}}
            </v-col>
            <v-col cols="4" md="6" class="d-flex align-center">
              <v-switch
              v-model="newCommentNotification"
              @update:model-value="updateSettingNewCommentNotification"
              hide-details="auto"
              color="primary"
              class="mt-0"
              ></v-switch>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="8" md=6 class="d-flex align-center">
              {{ $t('account.projectStatusChangeNotification')}}
            </v-col>
            <v-col cols="4" md=6 class="d-flex align-center">
              <v-switch
              v-model="changeProjectStatusNotification"
              @update:model-value="updateSettingChangeProjectStatusNotification"
              hide-details="auto"
              color="primary"
              class="mt-0"
              ></v-switch>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="8" md=6 class="d-flex align-center">
              {{ $t('account.systemNotifications')}}
            </v-col>
            <v-col cols="4" md=6 class="d-flex align-center">
              <v-switch
              v-model="receiveNotification"
              @update:model-value="updateSettingReceiveNotification"
              hide-details="auto"
              color="primary"
              class="mt-0"
              ></v-switch>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <h2 class="mb-3 mt-3 ml-3">{{ $t('account.serviceSettings')}}</h2>

        <v-card elevation=0 class="pa-4" style="">
          <v-row>
            <v-col cols="12" md="4"  class="d-flex align-center">
              {{ $t('account.interfaceLanguage')}}
            </v-col>
            <v-col cols="12" md="8" lg="6" xl=4 class="d-flex align-center">
              <VSelect
                  v-model="selectedLanguage"
                  :items="languages"
                  item-title="name"
                  item-value="code"
                  @update:model-value="changeLanguage"
                  variant="solo-filled"
                  flat
                  hide-details="auto"
              >
                <template v-slot:item="slotProps" >
                  <VListItem v-bind="slotProps.props">
                    <VListItemTitle>
                      <!-- <i :class="['mr-2', 'em', slotProps.item.flag]"></i> -->
                    {{slotProps.internalItem.name}}
                    </VListItemTitle>
                  </VListItem>
                </template>
              </VSelect>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <PrivacySettings :open-delete-dialog="shouldOpenDeleteDialog">

    </PrivacySettings>

    <AccountSessions></AccountSessions>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useCommonStore } from '@/stores/CommonStore.js'
import { useAccountStore } from '@/stores/AccountStore.js'
import { useSystemStore } from '@/stores/SystemStore.js'
import { useTenantStore } from '@/stores/TenantStore.js'
import { useMainStore } from '@/stores/MainStore.js'
import ChangePasswordDialog from './dialogs/ChangePasswordDialog.vue'
import EnableMFADialog from './dialogs/EnableMFADialog.vue'
import DisableMFADialog from './dialogs/DisableMFADialog.vue'
import PrivacySettings from './components/PrivacySettings.vue'
import AccountSessions from './components/AccountSessions.vue'
import { DELETE_ACCOUNT_SESSION_KEY } from '@/routes/account/AccountDeleteRequest.vue'

export default {
  name: "AccountIndex",
  components: {
    ChangePasswordDialog,
    EnableMFADialog,
    DisableMFADialog,
    PrivacySettings,
    AccountSessions
  },
  data() {
    const namePattern = /^[\p{L}\s-]{2,191}$/u;

    const shouldOpenDeleteDialog = !!window.sessionStorage.getItem(DELETE_ACCOUNT_SESSION_KEY);
    if (shouldOpenDeleteDialog) {
      window.sessionStorage.removeItem(DELETE_ACCOUNT_SESSION_KEY);
    }

    return {
      isFirstNameDisabled: true,
      isLastNameDisabled: true,
      isEmailAddressDisabled: true,
      firstName: "",
      lastName: "",
      emailAddress: "",
      firstNameError: null,
      lastNameError: null,
      emailAddressError: null,

      newCommentNotification: true,
      changeProjectStatusNotification: true,
      receiveNotification: true,

      selectedLanguage: 'en',
      shouldOpenDeleteDialog,
      isProcessing: false,
      isMFAEnabled: false,

      firstNameRules: [
        v => !!v || this.$t('account.requiredName'),
        v => namePattern.test(v) || this.$t('menus.onBoard.invalidName'),
      ],

      lastNameRules: [
        v => !!v || this.$t('account.requiredName'),
        v => namePattern.test(v) || this.$t('menus.onBoard.invalidName'),
      ],

      emailAddressRules: [
        (v) => !!v || this.$t('account.requiredEmailAddress'),
        (v) => (v || '').indexOf('@') > -1 || this.$t('account.invalidEmailAddress')
      ],
    }
  },
  watch: {
    user(newUser) {
      if (newUser) {
        this.refreshControlValues(newUser);
      }
    },
    cognitoUser(newUser) {
      if (newUser) {
        this.refreshMFAPreference();
      }
    }
  },
  mounted: function() {
    if (this.user) {
      this.refreshControlValues(this.user);
    }
  },
  methods: {
    openSubscriptionContactDialog() {
      const mainStore = useMainStore();
      mainStore.showSubscriptionContactDialog();
    },
    refreshControlValues(user) {
      this.firstName = user.firstname;
      this.lastName = user.lastname;
      this.emailAddress = user.email;

      this.newCommentNotification = true;
      this.changeProjectStatusNotification = true;
      this.receiveNotification = true;

      if (user.settings) {
        if (user.settings.new_comment_notification) {
          this.newCommentNotification = user.settings.new_comment_notification === '1';
          this.newCommentNotification = Boolean(Number(user.settings.new_comment_notification));
        }

        if (user.settings.project_status_change_notification) {
          this.changeProjectStatusNotification = user.settings.project_status_change_notification === '1';
        }

        if (user.settings.receive_notifications) {
          this.receiveNotification = user.settings.receive_notifications === '1';
        }

        if (user.settings.language) {
          this.selectedLanguage = user.settings.language;
        }
      }
    },
    refreshMFAPreference() {
      let self = this;
      const authStore = useAuthStore();
      authStore.fetchMFAPreference()
      .then(function(mfaPref) {
        if (mfaPref && mfaPref.enabled && mfaPref.enabled.length > 0) {
          self.isMFAEnabled = true;
        } else {
          self.isMFAEnabled = false;
        }
        console.log("MFA PREFERENCE", mfaPref)
      });
    },
    updateNotificationSetting(payload) {
      const self = this;

      const accountStore = useAccountStore();
      accountStore.updateNotificationSetting(payload)
          .then((data) => {
            if (data.name && data.value) {
              // Update the setting value in the store
              const authStore = useAuthStore();
              authStore.updateUserSettingValue({
                name: data.name,
                value: data.value,
              });
            }

            self.$showMessage(self.$t('account.settingChanged'));
          }).catch((error) => {
            if (error && error.response && error.response.data && error.response.data.error) {
              const localizedMessage = self.$localizeMessage(error.response.data.error, self.$t('backend.system-error'));
              self.$showMessage(localizedMessage, { type: 'error' });
            } else {
              console.log(error);
              self.$showMessage(self.$t('backend.system-error'), { type: 'error' });
            }
      });
    },
    toggleFirstNameField() {
      this.firstNameError = null;

      if (!this.isFirstNameDisabled) {
        const firstName = this.firstName.trim();
        if (firstName.length === 0) {
          this.firstNameError = this.$t('account.requiredName');
          return;
        }

        const payload = {
          userId: this.user.id,
          firstName,
        }

        const self = this;

        const accountStore = useAccountStore();
        accountStore.updateFirstName(payload)
          .then((data) => {
            this.isFirstNameDisabled = !this.isFirstNameDisabled;

            // Update the user object in the store
            const authStore = useAuthStore();
            authStore.updateDBUserProperty({
              property: 'firstname',
              value: data.firstName,
            });

            self.$showMessage(self.$t('account.nameChanged'));
          }).catch((error) => {
            if (error && error.response && error.response.data && error.response.data.error) {
              const response = error.response.data.error;
              self.firstNameError = self.$localizeMessage(response, response.message);
            } else {
              self.firstNameError = error.message;
              console.log(error);
            }
        });
      } else {
        this.isFirstNameDisabled = !this.isFirstNameDisabled;
      }
    },
    toggleLastNameField() {
      this.lastNameError = null;

      if (!this.isLastNameDisabled) {
        const lastName = this.lastName.trim();
        if (lastName.length === 0) {
          this.lastNameError = this.$t('account.requiredName');
          return;
        }

        const payload = {
          userId: this.user.id,
          lastName,
        }

        const self = this;

        const accountStore = useAccountStore();
        accountStore.updateLastName(payload)
          .then((data) => {
            this.isLastNameDisabled = !this.isLastNameDisabled;

            // Update the user object in the store
            const authStore = useAuthStore();
            authStore.updateDBUserProperty({
              property: 'lastname',
              value: data.lastName,
            });

            self.$showMessage(self.$t('account.nameChanged'));
          }).catch((error) => {
            if (error && error.response && error.response.data && error.response.data.error) {
              const response = error.response.data.error;
              self.lastNameError = self.$localizeMessage(response, response.message);
            } else {
              self.lastNameError = error.message;
              console.log(error);
            }
        });
      } else {
        this.isLastNameDisabled = !this.isLastNameDisabled;
      }
    },
    updateSettingNewCommentNotification() {
      console.log("this.newCommentNotification", this.newCommentNotification);
      const payload = {
        userId: this.user.id,
        name: 'new_comment_notification',
        value: this.newCommentNotification ? '1' : '0',
      }

      this.updateNotificationSetting(payload);
    },
    updateSettingChangeProjectStatusNotification() {
      const payload = {
        userId: this.user.id,
        name: 'project_status_change_notification',
        value: this.changeProjectStatusNotification ? '1' : '0',
      }

      this.updateNotificationSetting(payload);
    },
    updateSettingReceiveNotification() {
      const payload = {
        userId: this.user.id,
        name: 'receive_notifications',
        value: this.receiveNotification ? '1' : '0',
      }

      this.updateNotificationSetting(payload);
    },
    changeLanguage() {
      console.log("Language changed")
      const payload = {
        userId: this.user.id,
        language: this.selectedLanguage,
      }

      const self = this;

      const accountStore = useAccountStore();
      accountStore.updateLanguage(payload)
          .then((data) => {
            if (data.language) {
              self.$i18n.locale = data.language;

              // Update the setting value in the store
              const authStore = useAuthStore();
              authStore.refreshDBUser();
            }

            self.$showMessage(self.$t('account.languageChanged'));
          })
          .catch((error) => {
            if (error && error.response && error.response.data && error.response.data.error) {
              const localizedMessage = self.$localizeMessage(error.response.data.error, self.$t('backend.system-error'));
              self.$showMessage(localizedMessage, { type: 'error' });
            } else {
              console.log(error);
              self.$showMessage(self.$t('backend.system-error'), { type: 'error' });
            }
          });
    },
    cancelFirstNameEdit() {
      this.isFirstNameDisabled = !this.isFirstNameDisabled;
      this.firstName = this.user.firstname;
    },
    cancelLastNameEdit() {
      this.isLastNameDisabled = !this.isLastNameDisabled;
      this.lastName = this.user.lastname;
    },
  },
  computed: {
    user: function() {
      const authStore = useAuthStore();
      return authStore.dbUser;
    },
    languages: function() {
      const commonStore = useCommonStore();
      const languages = commonStore.interfaceLanguages;

      return languages.map((x) =>  {
        return {
          name: this.$t(`language.${x.name.toLowerCase()}`),
          code: x.code,
        }
      });
    },
    cognitoUser: function() {
      const authStore = useAuthStore();
      return authStore.user;
    },
    // isMFAEnabled: function() {
    //   return this.cognitoUser && this.cognitoUser.preferredMFA && this.cognitoUser.preferredMFA !== "NOMFA";
    // },
    getSubscriptionName: function() {
      const systemStore = useSystemStore();
      const subscription = systemStore.getSubscription;

      if (subscription && subscription.subscription && subscription.subscription.name) {
        return subscription.subscription.name;
      }

      if (this.subscriptionType === 'freetrial') {
        return this.$t('menus.subscriptions.freeTrial');
      }

      return null;
    },
    subscriptionType: function() {
      const authStore = useAuthStore();
      const activeTenant = authStore.getActiveTenant;

      if (activeTenant?.subscription?.subscriptionType) {
        return activeTenant.subscription.subscriptionType;
      }

      return null;
    },
    canShowSubscriptionType: function() {
      const tenantStore = useTenantStore();

      return !tenantStore.getRecurringSubscriptionTypes.includes(this.subscriptionType)
          && this.$canControlTenantConfig()
          && this.getSubscriptionName;
    },
  }

}
</script>
